import type {
  GapFillerData,
  GapOfferStep,
  GapReplies,
  GapRule,
} from './types';

/**
 * Gap Filler settings demo (amendment 26 §4–§6). Alamo Heights Dental.
 *
 * The seven rules are a **mutable store** (§0 — on staging *Add a rule*, *Edit*
 * and the drag handle all did nothing). The settings list, the rule page and the
 * "gaps nobody could be offered" fixes all read and write it.
 */

const RULES: GapRule[] = [
  {
    id: 'wants-earlier',
    name: 'Wants an earlier appointment',
    custom: false,
    enabled: true,
    who: 'From the PMS’s ASAP list, or asked ELVA to be told if a time opens up',
    description: 'Patients on the ASAP list, or who asked to be told if an earlier time opens up.',
    bookInto: 'Their appointment type',
    providers: 'Any provider',
    shortestGapMinutes: 30,
    patients: 38,
    matchedLastMonth: 21,
    unfilledLastMonth: 7,
  },
  {
    id: 'unscheduled-treatment',
    name: 'Unscheduled treatment',
    custom: false,
    enabled: true,
    who: 'Accepted a treatment plan and never booked it',
    description: 'Patients who accepted a treatment plan and never booked the work.',
    bookInto: 'Their accepted treatment',
    providers: 'General dentists',
    shortestGapMinutes: 45,
    patients: 112,
    matchedLastMonth: 18,
    unfilledLastMonth: 9,
  },
  {
    id: 'overdue-hygiene',
    name: 'Overdue hygiene',
    custom: false,
    enabled: true,
    who: 'Six months or more since their last cleaning',
    description: 'Patients whose last cleaning was six months ago or more.',
    bookInto: 'Hygiene',
    providers: 'Hygienists',
    shortestGapMinutes: 45,
    patients: 204,
    matchedLastMonth: 30,
    unfilledLastMonth: 8,
  },
  {
    id: 'crown-seat',
    name: 'Crown seat not booked',
    custom: true,
    enabled: true,
    who: 'Crown prepared, no seat appointment booked',
    description: 'Patients whose crown was prepared but who never booked the seat appointment.',
    bookInto: 'Crown seat',
    providers: 'Dr. Patel',
    shortestGapMinutes: 30,
    patients: 9,
    matchedLastMonth: 9,
    unfilledLastMonth: 6,
  },
  {
    id: 'missed-appt',
    name: 'Missed appointment',
    custom: false,
    enabled: true,
    who: 'Didn’t turn up in the last 30 days and hasn’t rebooked',
    description: 'Patients who missed an appointment in the last 30 days and haven’t rebooked.',
    bookInto: 'The type they missed',
    providers: 'Any provider',
    shortestGapMinutes: 30,
    patients: 21,
    matchedLastMonth: 14,
    unfilledLastMonth: 5,
  },
  {
    id: 'kids-after-school',
    name: 'Kids after school',
    custom: true,
    enabled: true,
    who: 'Children due for fluoride or sealants',
    description: 'Children due for fluoride or sealants.',
    bookInto: 'Child hygiene',
    providers: 'Hygienists',
    shortestGapMinutes: 30,
    window: { days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'], afterTime: '3:00 pm' },
    patients: 46,
    matchedLastMonth: 9,
    unfilledLastMonth: 6,
  },
  {
    id: 'invisalign-consult',
    name: 'Invisalign consult',
    custom: false,
    // Switched off — card 05 offers to switch it on for a gap with Dr. Rahimi.
    enabled: false,
    who: 'Asked about aligners, never booked',
    description: 'Patients who asked about aligners but never booked a consult.',
    bookInto: 'Invisalign consult',
    providers: 'Dr. Rahimi',
    shortestGapMinutes: 45,
    patients: 17,
    matchedLastMonth: 4,
    unfilledLastMonth: 3,
  },
];

/** The mutable rules store (§0). */
export const gapRulesStore: { rules: GapRule[] } = { rules: RULES.map((r) => ({ ...r })) };

export function gapRuleById(id: string): GapRule | undefined {
  return gapRulesStore.rules.find((r) => r.id === id);
}

export function reorderGapRules(from: number, to: number): void {
  const rules = [...gapRulesStore.rules];
  const [moved] = rules.splice(from, 1);
  if (moved === undefined) return;
  rules.splice(to, 0, moved);
  gapRulesStore.rules = rules;
}

export function toggleGapRule(id: string, on: boolean): void {
  gapRulesStore.rules = gapRulesStore.rules.map((r) => (r.id === id ? { ...r, enabled: on } : r));
}

export function saveGapRule(rule: GapRule): void {
  const exists = gapRulesStore.rules.some((r) => r.id === rule.id);
  gapRulesStore.rules = exists
    ? gapRulesStore.rules.map((r) => (r.id === rule.id ? rule : r))
    : [...gapRulesStore.rules, rule]; // new rules go to the bottom (§5)
}

export function deleteGapRule(id: string): void {
  gapRulesStore.rules = gapRulesStore.rules.filter((r) => r.id !== id);
}

/** A blank custom rule for `/rules/new`. */
export function blankGapRule(): GapRule {
  return {
    id: `rule-${String(Date.now())}`,
    name: '',
    custom: true,
    enabled: true,
    who: '',
    description: '',
    bookInto: 'What they’re due for',
    providers: 'Any provider',
    shortestGapMinutes: 30,
    patients: 0,
    matchedLastMonth: 0,
    unfilledLastMonth: 0,
  };
}

export const GAP_FILLER_SETTINGS: GapFillerData = {
  whichGaps: {
    lookAheadDays: 14,
    shortestGapMinutes: 30,
    noShowMinLeft: 30,
    shortGapsBetween: true,
  },
  offer: {
    howManyAtOnce: 5,
    thenWaitMinutes: 20,
    stopAfterPatients: 25,
    expiry: '1 hour before the gap',
    sendAfterHoursWithin24h: false,
    offerChips: [
      'Text, right away',
      'AI call after 10 min, if no reply · gaps under 3 hours',
      'Email instead of text when there’s no mobile',
    ],
  },
  neverOffered: [
    'Already has an appointment for what they need',
    'Being offered another gap right now',
    'Has had enough messages this fortnight',
    'Opted out of every channel',
    'The work wouldn’t fit the slot',
  ],
  uncovered: [
    {
      id: 'u1',
      what: 'Two gaps of 20 minutes',
      reason: 'Shorter than every rule’s shortest gap.',
      fix: { kind: 'shorten', ruleId: 'overdue-hygiene', label: 'Shorten a rule’s minimum' },
    },
    {
      id: 'u2',
      what: 'One gap with Dr. Rahimi',
      reason: 'No rule that’s on includes him.',
      fix: { kind: 'switch-on', ruleId: 'invisalign-consult', label: 'Switch on Invisalign consult' },
    },
  ],
};

/* ---- the offer sequence (§6) --------------------------------------------- */

export const GAP_OFFER_STEPS: GapOfferStep[] = [
  {
    id: 'o-text',
    channel: 'text',
    offsetMinutes: 0,
    rightAway: true,
    body: 'A {length} spot just opened with {provider} on {day} at {time}. Reply YES to book it.',
  },
  {
    id: 'o-call',
    channel: 'ai_call',
    offsetMinutes: 10,
    rightAway: false,
    onlyIf: { noReplyToText: true, gapWithinHours: 3 },
    brief:
      'Say a time has just opened and offer it: the day, the time, how long, and who with. If they want it, book it on the call. Friendly and quick — they may be at work.',
    body: '',
  },
  {
    id: 'o-email',
    channel: 'email',
    offsetMinutes: 0,
    rightAway: true,
    onlyIf: { noMobile: true },
    body: 'A time just opened on {day} at {time} with {provider} — about {length}. Reply to take it.',
  },
];

export const GAP_REPLIES: GapReplies = {
  whenTheyGetIt: 'You’re booked for {day} at {time} with {provider}. See you then!',
  whenSomeoneElseGotItFirst: 'Sorry — that time has just been taken. Want me to find you another?',
};

/** The two implant... no — two patients from today's 2 PM gap, for the call preview (§6). */
export const GAP_OFFER_PREVIEW = [
  {
    id: 'gp-grace',
    name: 'Grace Winslow',
    why: 'Overdue hygiene · English',
    spoken:
      'Hi Grace, this is the automated assistant for Alamo Heights Dental. A cleaning spot just opened today at 2 o’clock with Dr. Patel — about an hour. Would you like it?',
  },
  {
    id: 'gp-daniel',
    name: 'Daniel Ostrowski',
    why: 'Unscheduled crown · English',
    spoken:
      'Hi Daniel, this is the automated assistant for Alamo Heights Dental. We’ve just had an opening today at 2 with Dr. Patel — enough time for the crown you and Dr. Patel talked about. Would you like to take it?',
  },
];

/** Rule-page fields available to the offer (§6). */
export const GAP_OFFER_FIELDS = [
  'first_name',
  'day',
  'time',
  'length',
  'provider',
  'appointment_type',
  'practice_name',
  'practice_phone',
] as const;
